import type { CSSProperties } from "react";

export type Icon3DTone = "primary" | "accent" | "secondary" | "neutral" | "info" | "danger";

interface ToneSpec {
  fill: string;
  shade: string;
}

const TONES: Record<Icon3DTone, ToneSpec> = {
  primary: {
    fill: "from-primary-300 via-primary-500 to-primary-700",
    shade: "oklch(var(--primary-800))",
  },
  accent: {
    fill: "from-accent-300 via-accent-500 to-accent-700",
    shade: "oklch(var(--accent-800))",
  },
  secondary: {
    fill: "from-secondary-300 via-secondary-500 to-secondary-700",
    shade: "oklch(var(--secondary-800))",
  },
  neutral: {
    fill: "from-foreground-300 via-foreground-500 to-foreground-700",
    shade: "oklch(var(--foreground-800))",
  },
  info: {
    fill: "from-secondary-300 via-secondary-500 to-secondary-700",
    shade: "oklch(var(--secondary-800))",
  },
  danger: {
    fill: "from-accent-300 via-accent-500 to-accent-700",
    shade: "oklch(var(--accent-800))",
  },
};

interface Icon3DProps {
  icon: string;
  tone?: Icon3DTone;
  size?: number;
  className?: string;
  style?: CSSProperties;
}

/**
 * Icono con aspecto 3D caricatura: relleno con degradado, grosor
 * (extrusión) y una sombra suave que le da volumen. Usa los tokens
 * del tema, así que se adapta a modo claro y oscuro.
 */
export default function Icon3D({
  icon,
  tone = "primary",
  size = 22,
  className = "",
  style,
}: Icon3DProps) {
  const spec = TONES[tone];
  const lift = Math.max(1, Math.round(size * 0.07));

  return (
    <span
      className={`relative inline-flex items-center justify-center ${className}`}
      style={{ width: size, height: size, ...style }}
      aria-hidden="true"
    >
      {/* Capa de grosor (da la sensación de relieve 3D) */}
      <i
        className={`${icon} absolute inset-0 flex items-center justify-center leading-none`}
        style={{
          fontSize: size,
          color: spec.shade,
          transform: `translateY(${lift}px)`,
          WebkitTextStroke: `${Math.max(0.5, size * 0.03)}px ${spec.shade}`,
        }}
      />
      {/* Capa frontal con degradado y brillo */}
      <i
        className={`${icon} relative bg-gradient-to-br ${spec.fill} bg-clip-text leading-none text-transparent`}
        style={{
          fontSize: size,
          filter: `drop-shadow(0 ${lift}px ${Math.max(1, size * 0.06)}px rgba(0, 0, 0, 0.28))`,
        }}
      />
    </span>
  );
}